import { clone } from "lib/clone"

const DEFAULT_MODE = "content"

export function buildMacroLookup(macros = []) {
    const lookup = {}
    if (!Array.isArray(macros)) return lookup
    macros.forEach((macro) => {
        if (!macro?.id) return
        lookup[macro.id] = macro
    })
    return lookup
}

export function buildMacroNameLookup(macros = []) {
    const lookup = {}
    if (!Array.isArray(macros)) return lookup
    macros.forEach((macro) => {
        if (!macro?.id || !macro?.name) return
        lookup[macro.name] = macro.id
    })
    return lookup
}

export function replaceMacroTokensInString({ textOrHtml, macrosById = {}, mode = DEFAULT_MODE } = {}) {
    if (textOrHtml === null || textOrHtml === undefined) return textOrHtml
    const value = `${textOrHtml}`
    const ranges = getDollarDelimitedRanges(value)
    if (!ranges.length) return value

    let output = ""
    let cursor = 0
    for (const range of ranges) {
        const normalizedTokenId = normalizeEmbeddedWhitespace(range.tokenId)
        const macro = resolveMacroForToken(normalizedTokenId, macrosById)
        if (macro) {
            const replacement = getMacroReplacement(macro, normalizedTokenId, mode, value, range)
            output += value.slice(cursor, replacement.start)
            output += replacement.value
            cursor = replacement.end
        } else {
            output += value.slice(cursor, range.start)
            output += value.slice(range.start, range.end)
            cursor = range.end
        }
    }
    output += value.slice(cursor)
    return output
}

export function expandMacroTokens(textOrHtml, macroMap) {
    if (!macroMap || !textOrHtml) return textOrHtml
    return replaceMacroTokensInString({
        textOrHtml,
        macrosById: normalizeMacroMapForContentFallback(macroMap),
        mode: "content",
    })
}

export function replaceMacroLabelsWithIds({ textOrHtml, macrosById = {}, macrosByName = {} } = {}) {
    if (textOrHtml === null || textOrHtml === undefined) return textOrHtml
    const value = `${textOrHtml}`
    const ranges = getDollarDelimitedRanges(value)
    if (!ranges.length) return value

    let output = ""
    let cursor = 0
    for (const range of ranges) {
        output += value.slice(cursor, range.start)
        const tokenId = normalizeEmbeddedWhitespace(range.tokenId)
        if (macrosById?.[range.tokenId]) {
            output += `$${range.tokenId}$`
        } else if (macrosByName?.[range.tokenId]) {
            output += `$${macrosByName[range.tokenId]}$`
        } else if (macrosById?.[tokenId]) {
            output += `$${tokenId}$`
        } else if (macrosByName?.[tokenId]) {
            output += `$${macrosByName[tokenId]}$`
        } else {
            output += value.slice(range.start, range.end)
        }
        cursor = range.end
    }
    output += value.slice(cursor)
    return output
}

export function expandMacrosInSchedule(schedule, { macrosById = {}, mode = DEFAULT_MODE } = {}) {
    if (!schedule) return schedule
    if (!macrosById || Object.keys(macrosById).length === 0) return schedule
    const cloned = clone(schedule)

    if (cloned?.title) {
        cloned.title = replaceMacroTokensInString({ textOrHtml: cloned.title, macrosById, mode })
    }

    if (cloned?.intro) {
        cloned.intro = {
            ...cloned.intro,
            content: replaceMacroTokensInString({ textOrHtml: cloned.intro.content, macrosById, mode }),
            note: replaceMacroTokensInString({ textOrHtml: cloned.intro.note, macrosById, mode }),
        }
    }

    if (Array.isArray(cloned?.tasks)) {
        cloned.tasks = cloned.tasks.map((task) => ({
            ...task,
            title: replaceMacroTokensInString({ textOrHtml: task?.title, macrosById, mode }),
            content: replaceMacroTokensInString({ textOrHtml: task?.content, macrosById, mode }),
            note: replaceMacroTokensInString({ textOrHtml: task?.note, macrosById, mode }),
        }))
    }

    return cloned
}

function getMacroReplacement(macro, tokenId, mode, value, range) {
    if (mode === "label") {
        const label = macro?.name ?? tokenId
        return {
            start: range.start,
            end: range.end,
            value: `$${label}$`,
        }
    }

    return normalizeMacroContentForInsertion(getMacroContent(macro), value, range)
}

function resolveMacroForToken(tokenId, macrosById) {
    if (!tokenId) return null
    return macrosById?.[tokenId] ?? null
}

function getDollarDelimitedRanges(value = "") {
    if (!value) return []
    const ranges = []
    let index = 0

    while (index < value.length) {
        const start = value.indexOf("$", index)
        if (start === -1) break
        if (value[start + 1] === "$") {
            index = start + 2
            continue
        }

        const end = value.indexOf("$", start + 1)
        if (end === -1) break

        ranges.push({
            start,
            end: end + 1,
            tokenId: value.slice(start + 1, end),
        })
        index = end + 1
    }

    return ranges
}

function normalizeEmbeddedWhitespace(value = "") {
    return `${value}`.replace(/[\s\u00a0]+/g, "")
}

function getMacroContent(macro) {
    return macro?.content ?? macro?.contentHtml ?? ""
}

function normalizeMacroMapForContentFallback(macroMap = {}) {
    return Object.fromEntries(
        Object.entries(macroMap).flatMap(([id, macro]) => {
            const content = macro?.content || macro?.contentHtml || macro?.name
            if (!content) return []
            return [
                [
                    id,
                    {
                        ...macro,
                        content,
                    },
                ],
            ]
        })
    )
}

function normalizeMacroContentForInsertion(contentHtml = "", value = "", range = {}) {
    // Inline-stored content (no <p> tags — normalised at save time).
    // It must be treated as a single run of inline HTML regardless of context.
    if (isInlineStoredContent(contentHtml)) {
        const standaloneParagraphRange = findStandaloneParagraphRange(value, range)
        if (standaloneParagraphRange) {
            // Token is alone in its paragraph → re-wrap so output stays block-level.
            return {
                start: standaloneParagraphRange.start,
                end: standaloneParagraphRange.end,
                value: `<p>${contentHtml}</p>`,
            }
        }
        // Inline context (or no paragraph context at all) → insert directly.
        return {
            start: range.start,
            end: range.end,
            value: contentHtml,
        }
    }

    const standaloneParagraphRange = findStandaloneParagraphRange(value, range)
    if (standaloneParagraphRange) {
        return {
            start: standaloneParagraphRange.start,
            end: standaloneParagraphRange.end,
            value: contentHtml,
        }
    }

    if (isInlineParagraphContext(value, range)) {
        if (isSingleParagraphHtml(contentHtml)) {
            return {
                start: range.start,
                end: range.end,
                value: trimInlineMacroContent(unwrapSingleParagraph(contentHtml)),
            }
        }

        // Multi-paragraph content in inline context: split the surrounding paragraph so
        // the macro's block-level content is properly separated from the surrounding text.
        const split = splitParagraphAroundToken(value, range)
        if (split) {
            return {
                start: split.start,
                end: split.end,
                value: split.before + contentHtml + split.after,
            }
        }
    }

    return {
        start: range.start,
        end: range.end,
        value: contentHtml,
    }
}

/**
 * Returns true when the macro content contains no `<p>` tags at all.
 * This is the "inline-stored" form produced by save-time normalisation:
 * single-paragraph Jodit output has its outer `<p>` stripped before being
 * written to the database, so the stored value is plain inline HTML.
 */
function isInlineStoredContent(contentHtml = "") {
    if (!contentHtml) return false
    return !/<p[\s>]/i.test(contentHtml)
}

function isSingleParagraphHtml(contentHtml = "") {
    // Use (?:[^<]|<(?!\/p>))* to avoid matching across </p> boundaries,
    // so multi-paragraph content like <p>A</p><p>B</p> is NOT treated as single-paragraph.
    return /^\s*<p\b[^>]*>(?:[^<]|<(?!\/p>))*<\/p>\s*$/i.test(contentHtml)
}

function unwrapSingleParagraph(contentHtml = "") {
    return contentHtml.replace(/^\s*<p\b[^>]*>/i, "").replace(/<\/p>\s*$/i, "")
}

function trimInlineMacroContent(contentHtml = "") {
    return contentHtml.replace(/^(?:\s|&nbsp;|&#160;|\u00a0)+/gi, "").replace(/(?:\s|&nbsp;|&#160;|\u00a0)+$/gi, "")
}

function isInlineParagraphContext(value = "", range = {}) {
    if (!value) return false
    if (!Number.isFinite(range?.start) || !Number.isFinite(range?.end)) return false

    const paragraphBounds = findContainingParagraph(value, range)
    if (paragraphBounds) {
        const paragraphContent = value.slice(paragraphBounds.start, paragraphBounds.end)
        const relativeStart = range.start - paragraphBounds.start
        const relativeEnd = range.end - paragraphBounds.start
        const before = paragraphContent.slice(0, relativeStart)
        const after = paragraphContent.slice(relativeEnd)
        return hasMeaningfulInlineText(before) || hasMeaningfulInlineText(after)
    }

    const before = value.slice(0, range.start)
    const after = value.slice(range.end)
    return hasMeaningfulInlineText(before) || hasMeaningfulInlineText(after)
}

function findContainingParagraph(value = "", range = {}) {
    const searchStart = Number.isFinite(range?.start) ? range.start : -1
    const searchEnd = Number.isFinite(range?.end) ? range.end : -1
    if (searchStart < 0 || searchEnd < 0) return null

    const openingTagStart = value.lastIndexOf("<p", searchStart)
    if (openingTagStart === -1) return null

    const previousClosingTag = value.lastIndexOf("</p>", searchStart)
    if (previousClosingTag > openingTagStart) return null

    const openingTagEnd = value.indexOf(">", openingTagStart)
    if (openingTagEnd === -1 || openingTagEnd >= searchStart) return null

    const closingTagStart = value.indexOf("</p>", searchEnd)
    if (closingTagStart === -1) return null

    return {
        tagStart: openingTagStart,
        start: openingTagEnd + 1,
        end: closingTagStart,
        tagEnd: closingTagStart + "</p>".length,
    }
}

function hasMeaningfulInlineText(value = "") {
    const normalized = `${value}`
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;|&#160;|\u00a0/gi, " ")
        .trim()

    return normalized.length > 0
}

/**
 * When multi-paragraph macro content appears inline within a paragraph that also has other
 * text, we must split the surrounding paragraph so the block-level macro content sits
 * between two valid paragraphs rather than being nested inside one.
 *
 * Returns { start, end, before, after } where:
 *   - start/end: the span of the full surrounding <p>…</p> in value
 *   - before: closing fragment for text before the token, e.g. "<p>Start </p>" (or "")
 *   - after: opening fragment for text after the token, e.g. "<p> End</p>" (or "")
 */
function splitParagraphAroundToken(value = "", range = {}) {
    const paragraphBounds = findContainingParagraph(value, range)
    if (!paragraphBounds) return null

    const paragraphContent = value.slice(paragraphBounds.start, paragraphBounds.end)
    const relativeStart = range.start - paragraphBounds.start
    const relativeEnd = range.end - paragraphBounds.start
    const beforeText = paragraphContent.slice(0, relativeStart)
    const afterText = paragraphContent.slice(relativeEnd)

    const before = hasMeaningfulInlineText(beforeText) ? `<p>${beforeText}</p>` : ""
    const after = hasMeaningfulInlineText(afterText) ? `<p>${afterText}</p>` : ""

    return {
        start: paragraphBounds.tagStart,
        end: paragraphBounds.tagEnd,
        before,
        after,
    }
}

function findStandaloneParagraphRange(value = "", range = {}) {
    const paragraphBounds = findContainingParagraph(value, range)
    if (!paragraphBounds) return null

    const paragraphContent = value.slice(paragraphBounds.start, paragraphBounds.end)
    const relativeStart = range.start - paragraphBounds.start
    const relativeEnd = range.end - paragraphBounds.start
    const before = paragraphContent.slice(0, relativeStart)
    const after = paragraphContent.slice(relativeEnd)

    if (hasMeaningfulInlineText(before) || hasMeaningfulInlineText(after)) return null

    return {
        start: paragraphBounds.tagStart,
        end: paragraphBounds.tagEnd,
    }
}
